import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import axios from "axios";
import { API_BASE_URL, reportTakeAction, fetchIndividualReport, reactivateUsers } from "../../../api/endpoint";
import toast from "react-hot-toast";
import Loader from "../../../components/loader";

const GameReportDetailsPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const { auth } = useSelector((state) => state.login);

  const reportId = state?.reportId;

  const [report, setReport] = useState({});
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [actionPayload, setActionPayload] = useState({
    suspend: false,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reportId) return;
    loadDetails();
  }, [reportId]);

  const loadDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${fetchIndividualReport}${reportId}`, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      setReport(res?.data);
      if (res?.data?.attachmentUrl) {
        setAttachmentUrl(`${API_BASE_URL}public/images/${res?.data?.attachmentUrl}`);
      } else {
        setAttachmentUrl(null);
      }
    } catch (err) {
      console.error("Notification details error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!reportId) {
    return (
      <div className="container py-5 text-center text-muted">
        No report data.
        <button className="btn btn-link mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const hostId = report?.additionalData?.hostId;
  const participants = report?.additionalData?.participants || [];
  const isHostSuspended = participants?.find((p) => p?.userId === hostId)?.suspended;

  const fields = [
    { label: "Game Name", value: report?.additionalData?.gameName },
    { label: "Sport", value: report?.additionalData?.sportName },
    { label: "Host", value: report?.additionalData?.hostName },
    { label: "Format", value: report?.additionalData?.singularity },
    { label: "Level", value: report?.additionalData?.level },
    { label: "Start Time", value: report?.additionalData?.startTime },
    { label: "End Time", value: report?.additionalData?.endTime },
    { label: "Court", value: report?.additionalData?.courtName },
    { label: "City", value: report?.additionalData?.city || "—" },
    { label: "Description", value: report?.additionalData?.gameDescription },
  ];

  const submitAction = async () => {
    try {
      setSubmitting(true);
      const payload = {
        reportId: reportId,
        reportUserId: hostId,
        suspend: actionPayload.suspend,
        status: "ACTION_TAKEN",
        message: actionPayload.message,
      };
      await axios.post(reportTakeAction, payload, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      toast.success(actionPayload.suspend ? "Host has been suspended." : "Warning has been issued.");
      setShowActionModal(false);
      loadDetails();
    } catch (err) {
      console.error(err);
      toast.error("Failed to process action.");
    } finally {
      setSubmitting(false);
    }
  };

  const reactivateUser = async () => {
    try {
      setLoading(true);
      await axios.post(reactivateUsers, { authIds: [hostId] }, { headers: { Authorization: `Bearer ${auth}` } });
      setShowReactivateModal(false)
      toast.success("User reactivated successfully");
      loadDetails();
    } catch (error) {
      console.error("Error updating user status", error);
      toast.error(error?.response?.data?.user_description || "Error reactivating user status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <Button variant="secondary" onClick={() => navigate(-1)}>
        ← Back
      </Button>

      <h3 className="fw-bold mt-3 mb-4">Game Report Details</h3>

      <div className="card p-3 mb-4">
        <p>
          <strong>Reporter:</strong> {report.reporterName}
        </p>
        <p>
          <strong>Reason:</strong> {report.reason}
        </p>
      </div>

      <div className="card p-3 mb-4">
        <h5 className="fw-semibold mb-3">Game Info</h5>

        <table className="table table-borderless">
          <tbody>
            {fields.map((item, index) => (
              <tr key={index}>
                <td className="fw-semibold" style={{ width: "30%" }}>
                  {item.label}:
                </td>
                <td>{item.value ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {participants.length > 0 && (
        <div className="card p-3 mb-4">
          <h5 className="fw-semibold mb-3">Participants</h5>

          <table className="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Reliability</th>
              </tr>
            </thead>
            <tbody>
              {participants?.map((p) => (
                <tr key={p.userId}>
                  <td>
                    {p.name} {p.userId === hostId && <span className="text-primary fw-semibold">(Host)</span>}
                  </td>
                  <td>{p.email}</td>
                  <td>{p.reliability}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {attachmentUrl && (
        <div className="mt-4">
          <h6>Report Attachment:</h6>
          <div
            style={{
              maxWidth: "300px",
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "#f8f8f8",
              padding: "4px",
            }}
          >
            <img
              src={attachmentUrl}
              alt="attachment"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </div>
        </div>
      )}

      <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
        {isHostSuspended ? (
          <>
            <span className="text-danger fw-semibold">Host is suspended</span>
            <Button variant="success" onClick={() => setShowReactivateModal(true)}>
              Reactivate User
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="warning"
              onClick={() => {
                setActionPayload({ suspend: false, message: "" });
                setShowActionModal(true);
              }}
            >
              Issue Warning
            </Button>
            {}
            <Button
              variant="danger"
              onClick={() => {
                setActionPayload({ suspend: true, message: "" });
                setShowActionModal(true);
              }}
            >
              Suspend Host
            </Button>
          </>
        )}
      </div>

      <Modal show={showActionModal} onHide={() => !submitting && setShowActionModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{actionPayload.suspend ? "Suspend Host" : "Issue Warning"}</h5>
          <button className="btn-close" onClick={() => !submitting && setShowActionModal(false)} />
        </div>

        <div className="modal-body">
          <p>
            Are you sure you want to <strong>{actionPayload?.suspend ? "suspend" : "warn"}</strong> the host?
          </p>
          <label className="form-label fw-semibold">Message:</label>
          <textarea
            className="form-control"
            rows={4}
            value={actionPayload.message}
            onChange={(e) => setActionPayload({ ...actionPayload, message: e.target.value })}
            disabled={submitting}
          />
        </div>

        <div className="modal-footer">
          <Button variant="secondary" onClick={() => setShowActionModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant={actionPayload.suspend ? "danger" : "warning"} onClick={submitAction} disabled={submitting}>
            {submitting ? "Submitting..." : "Confirm"}
          </Button>
        </div>
      </Modal>

      {/* -------- Reactivate User Confirmation Modal -------- */}
      <Modal show={showReactivateModal} onHide={() => setShowReactivateModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">Reactivate User</h5>
          <button className="btn-close" onClick={() => setShowReactivateModal(false)}></button>
        </div>

        <div className="modal-body">
          <p>
            Are you sure you want to <strong>reactivate</strong> this user?
          </p>
        </div>

        <div className="modal-footer">
          <Button variant="secondary" onClick={() => setShowActionModal(false)}>
            Cancel
          </Button>

          <Button
            // variant={actionType === "WARN" ? "warning" : "danger"}
            onClick={reactivateUser}
            disabled={submitting}
          >
            Confirm
          </Button>
        </div>
      </Modal>
      {loading && <Loader />}
    </div>
  );
};

export default GameReportDetailsPage;
